import { useState, useEffect, useCallback } from 'react';
import { INITIAL_MESSAGE } from '../constants/prompts.js';
import { loadConversations, saveConversations, createNewConversationObject } from '../utils/storage.js';

export const useConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);

  // LocalStorageからデータを読み込み
  useEffect(() => {
    const savedConversations = loadConversations();
    if (savedConversations.length > 0) {
      setConversations(savedConversations);
      setCurrentConversationId(savedConversations[0].id);
    } else {
      // 初回アクセス時は新しい会話を作成
      const newConversation = createNewConversationObject(INITIAL_MESSAGE);
      setConversations([newConversation]);
      setCurrentConversationId(newConversation.id);
    }
  }, []);

  // データをLocalStorageに保存
  useEffect(() => {
    saveConversations(conversations);
  }, [conversations]);

  // 新しい会話を作成
  const createNewConversation = useCallback(() => {
    const newConversation = createNewConversationObject(INITIAL_MESSAGE);
    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversationId(newConversation.id);
  }, []);

  // 会話を削除
  const deleteConversation = useCallback((id) => {
    const shouldDelete = window.confirm('この会話を削除しますか？この操作は取り消せません。');
    if (!shouldDelete) return;

    setConversations(prev => {
      const filtered = prev.filter(c => c.id !== id);
      
      if (currentConversationId === id) {
        if (filtered.length > 0) {
          setCurrentConversationId(filtered[0].id);
        } else {
          // 会話がなくなったら新しい会話を作成
          const newConversation = createNewConversationObject(INITIAL_MESSAGE);
          setConversations([newConversation]);
          setCurrentConversationId(newConversation.id);
          return [newConversation];
        }
      }
      
      return filtered;
    });
  }, [currentConversationId]);

  // 会話にメッセージを追加
  const addMessageToConversation = useCallback((conversationId, message) => {
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        const updatedMessages = [...conv.messages, message];
        const updatedConv = {
          ...conv,
          messages: updatedMessages,
          updatedAt: new Date()
        };
        
        // タイトルを更新（最初のユーザーメッセージの場合）
        if (message.role === 'user' && conv.messages.filter(m => m.role === 'user').length === 0) {
          updatedConv.title = message.content.slice(0, 30) + (message.content.length > 30 ? '...' : '');
        }
        
        return updatedConv;
      }
      return conv;
    }));
  }, []);

  // 会話のサマリーを更新
  const updateConversationSummary = useCallback((conversationId, summary) => {
    if (!summary) return;
    
    setConversations(prev => prev.map(conv => 
      conv.id === conversationId 
        ? { ...conv, summary }
        : conv
    ));
  }, []);

  // メッセージを編集して保存
  const editMessageInConversation = useCallback((conversationId, messageId, newContent) => {
    const currentConv = conversations.find(c => c.id === conversationId);
    if (!currentConv) return null;

    const messageIndex = currentConv.messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return null;

    // 編集されたメッセージ以降を削除
    const messagesBeforeEdit = currentConv.messages.slice(0, messageIndex);
    const editedMessage = {
      ...currentConv.messages[messageIndex],
      content: newContent.trim(),
      timestamp: new Date()
    };

    // 会話を更新
    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          messages: [...messagesBeforeEdit, editedMessage],
          updatedAt: new Date()
        };
      }
      return conv;
    }));

    return { messagesBeforeEdit, editedMessage };
  }, [conversations]);

  // 指定したメッセージまでロールバック
  const rollbackToMessage = useCallback((conversationId, messageId) => {
    const currentConv = conversations.find(c => c.id === conversationId);
    if (!currentConv) return;

    const messageIndex = currentConv.messages.findIndex(m => m.id === messageId);
    if (messageIndex === -1) return;

    const messagesUntilRollback = currentConv.messages.slice(0, messageIndex + 1);

    setConversations(prev => prev.map(conv => {
      if (conv.id === conversationId) {
        return {
          ...conv,
          messages: messagesUntilRollback,
          updatedAt: new Date()
        };
      }
      return conv;
    }));
  }, [conversations]);

  const currentConversation = conversations.find(c => c.id === currentConversationId);

  return {
    conversations,
    currentConversation,
    currentConversationId,
    setCurrentConversationId,
    createNewConversation,
    deleteConversation,
    addMessageToConversation,
    updateConversationSummary,
    editMessageInConversation,
    rollbackToMessage
  };
};